// ── InterestStatementBatchPage ──
import { org } from "../../../shared/org";
import { useState, useEffect, useRef } from "react";
import { B } from "../../../shared/theme";
import { Btn, Card, FInput, Field, SectionHeader, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

export const InterestStatementBatchPage = () => {
    const { api, error, app } = useApp();
    const today = new Date();
    const fmt8  = d => d.toISOString().slice(0, 10);
    const firstOfPrior = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastOfPrior  = new Date(today.getFullYear(), today.getMonth(), 0);

    const [startdate, setStartdate] = useState(fmt8(firstOfPrior));
    const [enddate,   setEnddate]   = useState(fmt8(lastOfPrior));
    const [starting,  setStarting]  = useState(false);
    const [batchId,   setBatchId]   = useState(null);
    const [status,    setStatus]    = useState(null);
    const [err,       setErr]       = useState(null);
    const pollRef = useRef(null);

    const startBatch = async () => {
      setStarting(true); setErr(null); setBatchId(null); setStatus(null);
      try {
        const res = await api.post('startInterestStatementBatch', { startdate, enddate });
        if (!res.batchId) throw new Error('No batchId returned');
        setBatchId(res.batchId);
        setStatus({ total: res.total, pending: res.total, processing: 0, done: 0, error: 0, skipped: 0, complete: false, rows: [] });
        setStarting(false);
      } catch (e) {
        setErr(e.message); setStarting(false);
      }
    };

    // Poll for status
    useEffect(() => {
      if (!batchId) return;
      const poll = async () => {
        try {
          const s = await api.get('getBatchStatus?batchid=' + batchId);
          setStatus(s);
          if (s.complete) clearInterval(pollRef.current);
        } catch (e) { clearInterval(pollRef.current); }
      };
      poll();
      pollRef.current = setInterval(poll, 2000);
      return () => clearInterval(pollRef.current);
    }, [batchId]);

    const statusColor = s =>
      s === 'done'       ? '#0F6E56' :
      s === 'error'      ? '#993C1D' :
      s === 'skipped'    ? '#888780' :
      s === 'processing' ? '#185FA5' : '#BA7517';

    const statusBg = s =>
      s === 'done'       ? '#E1F5EE' :
      s === 'error'      ? '#FAECE7' :
      s === 'skipped'    ? '#F1EFE8' :
      s === 'processing' ? '#E6F1FB' : '#FAEEDA';

    const pct = status && status.total > 0
      ? Math.round(((status.done + status.error + status.skipped) / status.total) * 100)
      : 0;

    return <>
      <SectionHeader title="Generate All Interest Statements" />

      <Card style={{ padding:20, marginBottom:20 }}>
        <div style={{ fontSize:13, color:'var(--color-text-secondary)', marginBottom:16 }}>
          Generates interest statements for all active accounts with a Box folder configured
          and uploads them directly to each account's Box folder.
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:16, alignItems:'flex-end' }}>
          <Field label="Beginning Date">
            <FInput type="date" value={startdate} onChange={setStartdate} />
          </Field>
          <Field label="Ending Date">
            <FInput type="date" value={enddate} onChange={setEnddate} />
          </Field>
          <Btn
            variant="primary"
            disabled={starting || (status && !status.complete)}
            onClick={startBatch}
          >
            {starting ? 'Starting...' : status && !status.complete ? 'Running...' : '▶ Generate All'}
          </Btn>
        </div>
        {err && <div style={{ marginTop:12, background:'#FAECE7', border:'0.5px solid #E8B4A8', borderRadius:6, padding:'10px 14px', fontSize:13, color:'#993C1D' }}>⚠️ {err}</div>}
      </Card>

      {/* Progress section */}
      {status && <>
        <Card style={{ padding:20, marginBottom:20 }}>
          {/* Progress bar */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <div style={{ fontSize:13, fontWeight:500 }}>
              {status.complete ? '✅ Complete' : '⏳ Processing...'}
            </div>
            <div style={{ fontSize:12, color:'var(--color-text-secondary)' }}>
              {status.done + status.error + status.skipped} / {status.total}
            </div>
          </div>
          <div style={{ height:8, background:'var(--color-background-secondary)', borderRadius:4, marginBottom:16 }}>
            <div style={{ height:8, width:pct+'%', background:B.cyan, borderRadius:4, transition:'width 0.3s ease' }} />
          </div>

          {/* Summary counts */}
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
            {[
              ['Pending',    status.pending,    '#BA7517', '#FAEEDA'],
              ['Processing', status.processing, '#185FA5', '#E6F1FB'],
              ['Done',       status.done,       '#0F6E56', '#E1F5EE'],
              ['Error',      status.error,      '#993C1D', '#FAECE7'],
              ['Skipped',    status.skipped,    '#888780', '#F1EFE8'],
            ].map(([label, count, color, bg]) => count > 0 && (
              <div key={label} style={{ background:bg, color, padding:'4px 12px', borderRadius:6, fontSize:12, fontWeight:500 }}>
                {label}: {count}
              </div>
            ))}
          </div>

          {/* Per-account status table */}
          {status.rows.length > 0 && (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <Th>Account</Th>
                <Th>Status</Th>
                <Th>Message</Th>
                <Th>Box File</Th>
                <Th>Updated</Th>
              </tr></thead>
              <tbody>
                {status.rows.map(r => (
                  <tr key={r.id}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--color-background-secondary)'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}>
                    <Td style={{ fontWeight:500 }}>{r.companyname}</Td>
                    <Td>
                      <span style={{ color:statusColor(r.status), background:statusBg(r.status), padding:'2px 8px', borderRadius:4, fontSize:12, fontWeight:500 }}>
                        {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                      </span>
                    </Td>
                      <Td style={{ color:'var(--color-text-secondary)', fontSize:12 }}>{r.message||'—'}</Td>
                      <Td style={{ fontSize:12 }}>
                        {r.box_file_id ? <a href={`${org("boxBase")}/file/${r.box_file_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color:B.cyan, textDecoration:'none', fontWeight:500 }}
                              onMouseEnter={e=>e.currentTarget.style.textDecoration='underline'}
                              onMouseLeave={e=>e.currentTarget.style.textDecoration='none'}
                            > ✓ View in Box </a>
                          : '—'}
                      </Td>
                    <Td style={{ color:'var(--color-text-secondary)', fontSize:12 }}>
                      {r.updatedat ? new Date(r.updatedat).toLocaleTimeString() : '—'}
                    </Td>

                    
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </>}
    </>;
  };
